/**
 * Assumptions Resolver
 *
 * Resolves assumptions from the registry with version selection based on date.
 * Never returns raw values — always includes metadata for traceability.
 *
 * Usage:
 *   const prices = resolveAssumptions('energy_prices', { asOf: new Date() });
 *   // Returns: { values: {...}, meta: { version, source, ... } }
 */

import type {
  AssumptionSet,
  AssumptionSetName,
  AssumptionTypeMap,
  ResolvedAssumption,
  EnergyPricesValues,
  CarbonFactorsValues,
} from './types';

// ==================== FALLBACK CONSTANTS ====================
// Used when registry fails to load. These match the values that were
// previously hardcoded in year1Impact.ts and carbonEmissions.ts.

const FALLBACK_ENERGY_PRICES: EnergyPricesValues = {
  gas_unit_rate_p_per_kwh: 5.93,
  electricity_unit_rate_p_per_kwh: 27.69,
  gas_standing_charge_p_per_day: 31.74,
  electricity_standing_charge_p_per_day: 56.19,
};

const FALLBACK_CARBON_FACTORS: CarbonFactorsValues = {
  gas: 0.183,
  oil: 0.246,
  lpg: 0.214,
  electricity_grid: {
    '2024': 0.207,
    '2025': 0.185,
    '2026': 0.160,
    '2030': 0.085,
    '2040': 0.050,
    '2050': 0.020,
  },
};

// Import JSON files
import energyPrices from '../../../data/assumptions/energy_prices.json';
import carbonFactors from '../../../data/assumptions/carbon_factors.json';
import systemLifespans from '../../../data/assumptions/system_lifespans.json';
import efficiencyBands from '../../../data/assumptions/efficiency_bands.json';
import demandModels from '../../../data/assumptions/demand_models.json';
import behaviouralDefaults from '../../../data/assumptions/behavioural_defaults.json';

// ==================== REGISTRY ====================

const ASSUMPTION_SETS: Record<AssumptionSetName, AssumptionSet> = {
  energy_prices: energyPrices as AssumptionSet,
  carbon_factors: carbonFactors as AssumptionSet,
  system_lifespans: systemLifespans as AssumptionSet,
  efficiency_bands: efficiencyBands as AssumptionSet,
  demand_models: demandModels as AssumptionSet,
  behavioural_defaults: behaviouralDefaults as AssumptionSet,
};

// ==================== OPTIONS ====================

export interface ResolveOptions {
  /**
   * Date to resolve assumptions for.
   * Selects the latest version valid at this date.
   * Defaults to current date.
   */
  asOf?: Date;

  /**
   * Specific version ID to use (overrides asOf).
   * Use when you need exact reproducibility.
   */
  versionId?: string;
}

// ==================== RESOLVER ====================

/**
 * Resolve assumptions with full type safety and metadata.
 *
 * @param setName - The assumption set to resolve
 * @param options - Resolution options (date or specific version)
 * @returns Resolved values with metadata for traceability
 *
 * @example
 * ```typescript
 * const prices = resolveAssumptions('energy_prices');
 * console.log(prices.values.electricity_unit_rate);
 * console.log(prices.meta.source); // "Ofgem Price Cap Q1 2026"
 * ```
 */
export function resolveAssumptions<K extends AssumptionSetName>(
  setName: K,
  options: ResolveOptions = {}
): ResolvedAssumption<AssumptionTypeMap[K]> {
  const assumptionSet = ASSUMPTION_SETS[setName];

  if (!assumptionSet) {
    throw new Error(`Unknown assumption set: ${setName}`);
  }

  const { asOf = new Date(), versionId } = options;

  // Find the version
  let version;

  if (versionId) {
    // Exact version match
    version = assumptionSet.versions.find((v) => v.id === versionId);
    if (!version) {
      throw new Error(
        `Version ${versionId} not found in assumption set ${setName}`
      );
    }
  } else {
    // Find latest version valid at asOf date
    const asOfTime = asOf.getTime();
    const validVersions = assumptionSet.versions
      .filter((v) => new Date(v.valid_from).getTime() <= asOfTime)
      .sort(
        (a, b) =>
          new Date(b.valid_from).getTime() - new Date(a.valid_from).getTime()
      );

    version = validVersions[0];

    if (!version) {
      // Fallback to earliest version if asOf is before all versions
      version = assumptionSet.versions[0];
      if (!version) {
        throw new Error(`No versions found in assumption set ${setName}`);
      }
    }
  }

  return {
    values: version.values as AssumptionTypeMap[K],
    meta: {
      assumption_set: assumptionSet.meta.assumption_set,
      version: version.id,
      source: version.source.name,
      source_url: version.source.url,
      last_checked: version.last_checked,
      valid_from: version.valid_from,
    },
  };
}

/**
 * Get all available assumption set names
 */
export function getAvailableAssumptionSets(): AssumptionSetName[] {
  return Object.keys(ASSUMPTION_SETS) as AssumptionSetName[];
}

/**
 * Get all versions for an assumption set (for debugging/admin)
 */
export function getVersionHistory(setName: AssumptionSetName): Array<{
  id: string;
  valid_from: string;
  last_checked: string;
}> {
  const assumptionSet = ASSUMPTION_SETS[setName];
  if (!assumptionSet) {
    throw new Error(`Unknown assumption set: ${setName}`);
  }

  return assumptionSet.versions.map((v) => ({
    id: v.id,
    valid_from: v.valid_from,
    last_checked: v.last_checked,
  }));
}

/**
 * Safely resolve assumptions with fallback behavior.
 * If the registry fails to load, returns fallback constants with fallback_used: true.
 *
 * Use this in production code paths where you can't afford to throw.
 */
export function resolveAssumptionsSafe<K extends AssumptionSetName>(
  setName: K,
  options: ResolveOptions = {}
): ResolvedAssumption<AssumptionTypeMap[K]> {
  try {
    return resolveAssumptions(setName, options);
  } catch (error) {
    // Return fallback values for known sets
    if (setName === 'energy_prices') {
      return {
        values: FALLBACK_ENERGY_PRICES as AssumptionTypeMap[K],
        meta: {
          assumption_set: 'energy_prices',
          version: 'fallback',
          source: 'Inline fallback constants',
          source_url: null,
          last_checked: '2026-01-01',
          valid_from: '2024-01-01',
          fallback_used: true,
        },
      };
    }
    if (setName === 'carbon_factors') {
      return {
        values: FALLBACK_CARBON_FACTORS as AssumptionTypeMap[K],
        meta: {
          assumption_set: 'carbon_factors',
          version: 'fallback',
          source: 'Inline fallback constants',
          source_url: null,
          last_checked: '2026-01-01',
          valid_from: '2024-01-01',
          fallback_used: true,
        },
      };
    }
    // Re-throw for unknown sets (they should fix their config)
    throw error;
  }
}

/**
 * Check if assumptions need review based on last_checked date
 * Returns assumption sets that haven't been checked in the specified days
 */
export function getStaleAssumptions(
  maxAgeDays: number = 90
): Array<{ set: AssumptionSetName; lastChecked: string; daysSince: number }> {
  const now = new Date();
  const stale: Array<{ set: AssumptionSetName; lastChecked: string; daysSince: number }> = [];

  for (const [name, assumptionSet] of Object.entries(ASSUMPTION_SETS)) {
    const latestVersion = assumptionSet.versions[assumptionSet.versions.length - 1];
    if (latestVersion) {
      const lastChecked = new Date(latestVersion.last_checked);
      const daysSince = Math.floor(
        (now.getTime() - lastChecked.getTime()) / (1000 * 60 * 60 * 24)
      );
      if (daysSince > maxAgeDays) {
        stale.push({
          set: name as AssumptionSetName,
          lastChecked: latestVersion.last_checked,
          daysSince,
        });
      }
    }
  }

  return stale;
}
